import config from '../config';
import { devLog } from '../utils/devLog';
import { authFetchInit } from './authSession';

class DraftStateApi {
  constructor() {
    this.baseUrl = config.API_URL;
  }

  async makeRequest(endpoint, options = {}, currentUser = null) {
    const userId = currentUser?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const url = `${this.baseUrl}${endpoint}`;
    const defaultOptions = authFetchInit({
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const finalOptions = { ...defaultOptions, ...options };
    const separator = endpoint.includes('?') ? '&' : '?';
    const queryUserId = `${separator}user_id=${userId}`;

    // Add user_id as query parameter (backend expects it for both GET and POST for roster/customization endpoints)
    if (finalOptions.method === 'GET') {
      finalOptions.url = `${url}${queryUserId}`;
    } else {
      finalOptions.url = `${url}${queryUserId}`;
      if (finalOptions.body) {
        const body = JSON.parse(finalOptions.body);
        body.user_id = userId;
        finalOptions.body = JSON.stringify(body);
      }
    }

    try {
      const response = await fetch(finalOptions.url || url, finalOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('DraftStateApi request failed:', error);
      throw error;
    }
  }

  // Get draft completion status for a league
  async getDraftCompletionStatus(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-completion`, {}, currentUser);
  }

  // Set draft completion status for a league
  async setDraftCompletionStatus(leagueId, isComplete, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-completion`, {
      method: 'POST',
      body: JSON.stringify({ is_complete: isComplete })
    }, currentUser);
  }

  // Get team count for a league
  async getTeamCount(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/team-count`, {}, currentUser);
  }

  // Set team count for a league
  async setTeamCount(leagueId, teamCount, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/team-count`, {
      method: 'POST',
      body: JSON.stringify({ team_count: teamCount })
    }, currentUser);
  }

  // Get draft order for a league
  async getDraftOrder(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-order`, {}, currentUser);
  }

  // Set draft order for a league
  async setDraftOrder(leagueId, draftOrder, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-order`, {
      method: 'POST',
      body: JSON.stringify({ draft_order: draftOrder })
    }, currentUser);
  }

  // Get draft timer for a league
  async getDraftTimer(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-timer`, {}, currentUser);
  }

  // Set draft timer for a league
  async setDraftTimer(leagueId, timerData, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-timer`, {
      method: 'POST',
      body: JSON.stringify({ timer_data: timerData })
    }, currentUser);
  }

  // Migration helper: Get draft completion status from localStorage (fallback)
  getDraftCompletionFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_draft_complete_${leagueId}`);
      return stored === 'true';
    } catch (e) {
      console.error('Error reading draft completion from localStorage:', e);
      return false;
    }
  }

  // Migration helper: Get team count from localStorage (fallback)
  getTeamCountFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_team_count_${leagueId}`);
      return stored ? parseInt(stored, 10) : null;
    } catch (e) {
      console.error('Error reading team count from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get draft order from localStorage (fallback)
  getDraftOrderFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_draft_order_${leagueId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading draft order from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get draft timer from localStorage (fallback)
  getDraftTimerFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_draft_timer_${leagueId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading draft timer from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get draft timer duration from localStorage (fallback)
  getDraftTimerDurationFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_draft_timer_duration_${leagueId}`);
      return stored ? parseInt(stored, 10) : null;
    } catch (e) {
      console.error('Error reading draft timer duration from localStorage:', e);
      return null;
    }
  }

  // Get draft chat messages for a league
  async getDraftChatMessages(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-chat`, {}, currentUser);
  }

  // Add draft chat message
  async addDraftChatMessage(leagueId, message, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/draft-chat`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: currentUser.id,
        message: message
      })
    }, currentUser);
  }

  // Migration helper: Get draft chat from localStorage (fallback)
  getDraftChatFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_draft_chat_${leagueId}`);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error reading draft chat from localStorage:', e);
      return [];
    }
  }

  // Get week processing data for a league
  async getWeekProcessing(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/week-processing`, {}, currentUser);
  }

  // Set week processing data for a league
  async setWeekProcessing(leagueId, weekData, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/week-processing`, {
      method: 'POST',
      body: JSON.stringify({ week_data: weekData })
    }, currentUser);
  }

  // Migration helper: Get week processing from localStorage (fallback)
  getWeekProcessingFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_week_processing_${leagueId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading week processing from localStorage:', e);
      return null;
    }
  }

  // Get week records data for a league
  async getWeekRecords(leagueId, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/week-records`, {}, currentUser);
  }

  // Set week records data for a league
  async setWeekRecords(leagueId, weekRecords, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/week-records`, {
      method: 'POST',
      body: JSON.stringify({ week_records: weekRecords })
    }, currentUser);
  }

  // Migration helper: Get week records from localStorage (fallback)
  getWeekRecordsFromLocalStorage(leagueId) {
    try {
      const stored = localStorage.getItem(`ffb_week_records_${leagueId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading week records from localStorage:', e);
      return null;
    }
  }

  // Get team roster data for a league
  async getTeamRoster(leagueId, teamName, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/rosters?team_name=${encodeURIComponent(teamName)}`, {}, currentUser);
  }

  // Set team roster data for a league
  async setTeamRoster(leagueId, teamName, rosterData, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/rosters`, {
      method: 'POST',
      body: JSON.stringify({ 
        team_name: teamName,
        roster_data: rosterData 
      })
    }, currentUser);
  }

  // Migration helper: Get team roster from localStorage (fallback)
  getTeamRosterFromLocalStorage(leagueId, teamName) {
    try {
      // Try both formats: ffb_team_roster_ and ffb_roster_
      const stored1 = localStorage.getItem(`ffb_team_roster_${leagueId}_${teamName}`);
      const stored2 = localStorage.getItem(`ffb_roster_${leagueId}_${teamName}`);
      const stored = stored1 || stored2;
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading team roster from localStorage:', e);
      return null;
    }
  }

  // Get team customization data for a league
  async getTeamCustomization(leagueId, teamName, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/team-customization?team_name=${encodeURIComponent(teamName)}`, {}, currentUser);
  }

  // Set team customization data for a league
  async setTeamCustomization(leagueId, teamName, displayName, iconKey, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/team-customization`, {
      method: 'POST',
      body: JSON.stringify({ 
        team_name: teamName,
        display_name: displayName,
        icon_key: iconKey
      })
    }, currentUser);
  }

  // Migration helper: Get team name from localStorage (fallback)
  getTeamNameFromLocalStorage(leagueId, teamName) {
    try {
      const stored = localStorage.getItem(`ffb_team_name_${leagueId}_${teamName}`);
      return stored || null;
    } catch (e) {
      console.error('Error reading team name from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get FAAB budget from localStorage (fallback)
  getFaabBudgetFromLocalStorage(teamName) {
    try {
      const stored = localStorage.getItem(`ffb_faab_budget_${teamName}`);
      return stored ? parseInt(stored, 10) : null;
    } catch (e) {
      console.error('Error reading FAAB budget from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get FAAB bids from localStorage (fallback)
  getFaabBidsFromLocalStorage(teamName) {
    try {
      const stored = localStorage.getItem(`ffb_faab_bids_${teamName}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading FAAB bids from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get FAAB history from localStorage (fallback)
  getFaabHistoryFromLocalStorage(teamName) {
    try {
      const stored = localStorage.getItem(`ffb_faab_history_${teamName}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading FAAB history from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get waiver history from localStorage (fallback)
  getWaiverHistoryFromLocalStorage(teamName) {
    try {
      const stored = localStorage.getItem(`ffb_waiver_history_${teamName}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading waiver history from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get random player points from localStorage (fallback)
  getRandomPlayerPointsFromLocalStorage(leagueId, team, week, slot, playerId) {
    try {
      const key = `ffb_rand_${leagueId}_${team}_${week}_${slot}_${playerId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error reading random player points from localStorage:', e);
      return null;
    }
  }

  // Migration helper: Get week processed status from localStorage (fallback)
  getWeekProcessedFromLocalStorage(leagueId, week) {
    try {
      const key = `ffb_week_processed_${leagueId}_${week}`;
      const stored = localStorage.getItem(key);
      return stored === 'true';
    } catch (e) {
      console.error('Error reading week processed status from localStorage:', e);
      return false;
    }
  }

  // Get waiver transactions for a league
  async getWaiverTransactions(leagueId, teamName = null, currentUser) {
    let endpoint = `/leagues/${leagueId}/waiver-transactions`;
    if (teamName) {
      endpoint += `?team_name=${encodeURIComponent(teamName)}`;
    }
    return this.makeRequest(endpoint, {}, currentUser);
  }

  // Create waiver transaction
  async createWaiverTransaction(leagueId, transactionData, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/waiver-transactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transactionData)
    }, currentUser);
  }

  // Get week processed status for a league
  async getWeekProcessed(leagueId, week = null, currentUser) {
    let endpoint = `/leagues/${leagueId}/week-processed`;
    if (week) {
      endpoint += `?week=${week}`;
    }
    return this.makeRequest(endpoint, {}, currentUser);
  }

  // Set week processed status for a league
  async setWeekProcessed(leagueId, week, processed, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/week-processed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        week: week,
        processed: processed
      })
    }, currentUser);
  }

  // Get random player points for a league
  async getRandomPlayerPoints(leagueId, teamName = null, week = null, slot = null, playerId = null, currentUser) {
    let endpoint = `/leagues/${leagueId}/random-player-points`;
    const params = [];
    if (teamName) params.push(`team_name=${encodeURIComponent(teamName)}`);
    if (week) params.push(`week=${week}`);
    if (slot) params.push(`slot=${encodeURIComponent(slot)}`);
    if (playerId) params.push(`player_id=${encodeURIComponent(playerId)}`);
    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }
    return this.makeRequest(endpoint, {}, currentUser);
  }

  // Set random player points for a league
  async setRandomPlayerPoints(leagueId, pointsData, currentUser) {
    return this.makeRequest(`/leagues/${leagueId}/random-player-points`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pointsData)
    }, currentUser);
  }

  normalizeTeamSlotName(teamName) {
    if (!teamName) return teamName;
    const trimmed = String(teamName).trim();
    const compact = trimmed.replace(/\s+/g, '');
    const match = compact.match(/^Team(\d+)$/i) || trimmed.match(/^Team\s*(\d+)$/i);
    return match ? `Team${parseInt(match[1], 10)}` : trimmed;
  }

  clearDraftOnlyLocalStorage(leagueId) {
    try {
      localStorage.removeItem(`ffb_draft_complete_${leagueId}`);
      localStorage.removeItem(`ffb_draft_order_${leagueId}`);
      localStorage.removeItem(`ffb_draft_timer_${leagueId}`);
      localStorage.removeItem(`ffb_draft_timer_duration_${leagueId}`);
      for (let i = 1; i <= 20; i++) {
        localStorage.removeItem(`ffb_team_name_${leagueId}_Team${i}`);
      }
    } catch (e) {
      console.error('Error clearing draft localStorage keys:', e);
    }
  }

  async migrateDraftLocalStorageOnly(leagueId, currentUser) {
    if (!currentUser?.id) return;

    const migratedKey = `ffb_draft_storage_migrated_${leagueId}`;
    if (localStorage.getItem(migratedKey) === 'true') {
      this.clearDraftOnlyLocalStorage(leagueId);
      return;
    }

    try {
      const isComplete = this.getDraftCompletionFromLocalStorage(leagueId);
      if (isComplete) {
        await this.setDraftCompletionStatus(leagueId, true, currentUser);
        devLog(`Migrated draft completion status for league ${leagueId}`);
      }

      const draftOrder = this.getDraftOrderFromLocalStorage(leagueId);
      if (draftOrder && draftOrder.length > 0) {
        await this.setDraftOrder(leagueId, draftOrder, currentUser);
        devLog(`Migrated draft order for league ${leagueId}`);
      }

      const timerData = this.getDraftTimerFromLocalStorage(leagueId);
      const timerDuration = this.getDraftTimerDurationFromLocalStorage(leagueId);
      if (timerData || timerDuration) {
        const combinedTimerData = {
          ...timerData,
          duration: timerDuration || timerData?.duration || 60,
        };
        await this.setDraftTimer(leagueId, combinedTimerData, currentUser);
        devLog(`Migrated draft timer for league ${leagueId}`);
      }

      for (let i = 1; i <= 20; i++) {
        const teamName = `Team${i}`;
        const teamDisplayName = this.getTeamNameFromLocalStorage(leagueId, teamName);
        if (teamDisplayName) {
          await this.setTeamCustomization(leagueId, teamName, teamDisplayName, null, currentUser);
          devLog(`Migrated team display name for ${teamName} in league ${leagueId}`);
        }
      }

      localStorage.setItem(migratedKey, 'true');
      this.clearDraftOnlyLocalStorage(leagueId);
    } catch (error) {
      console.error(`Error migrating draft localStorage for league ${leagueId}:`, error);
    }
  }

  async getDraftCompletionStatusWithMigration(leagueId, currentUser) {
    await this.migrateDraftLocalStorageOnly(leagueId, currentUser);
    return this.getDraftCompletionStatus(leagueId, currentUser);
  }

  // Migration helper: Remove localStorage data after successful migration
  removeLocalStorageData(leagueId) {
    try {
      localStorage.removeItem(`ffb_draft_complete_${leagueId}`);
      localStorage.removeItem(`ffb_team_count_${leagueId}`);
      localStorage.removeItem(`ffb_draft_order_${leagueId}`);
      localStorage.removeItem(`ffb_draft_timer_${leagueId}`);
      localStorage.removeItem(`ffb_draft_timer_duration_${leagueId}`);
      localStorage.removeItem(`ffb_draft_chat_${leagueId}`);
      localStorage.removeItem(`ffb_week_processing_${leagueId}`);
      localStorage.removeItem(`ffb_week_records_${leagueId}`);
      
      // Remove team roster data for all teams (Team1, Team2, etc.)
      for (let i = 1; i <= 20; i++) { // Support up to 20 teams
        localStorage.removeItem(`ffb_team_roster_${leagueId}_Team${i}`);
        localStorage.removeItem(`ffb_roster_${leagueId}_Team${i}`);
        localStorage.removeItem(`ffb_team_name_${leagueId}_Team${i}`);
      }
      
      // Remove FAAB data for all teams
      for (let i = 1; i <= 20; i++) { // Support up to 20 teams
        const teamName = `Team${i}`;
        localStorage.removeItem(`ffb_faab_budget_${teamName}`);
        localStorage.removeItem(`ffb_faab_bids_${teamName}`);
        localStorage.removeItem(`ffb_faab_history_${teamName}`);
        localStorage.removeItem(`ffb_waiver_history_${teamName}`);
      }
      
      // Remove random player points data for all weeks and teams
      const allKeys = Object.keys(localStorage);
      const randomPointsKeys = allKeys.filter(key => 
        key.startsWith(`ffb_rand_${leagueId}_`)
      );
      randomPointsKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Remove week processed status for all weeks
      for (let week = 1; week <= 17; week++) {
        localStorage.removeItem(`ffb_week_processed_${leagueId}_${week}`);
      }
      
      devLog(`Removed localStorage data for league ${leagueId}`);
    } catch (e) {
      console.error('Error removing localStorage data:', e);
    }
  }

  // Migration helper: Migrate localStorage data to database
  async migrateLocalStorageData(leagueId, currentUser) {
    try {
      devLog(`Starting localStorage migration for league ${leagueId}`);
      
      // Migrate draft completion status
      const isComplete = this.getDraftCompletionFromLocalStorage(leagueId);
      if (isComplete) {
        await this.setDraftCompletionStatus(leagueId, true, currentUser);
        devLog(`Migrated draft completion status for league ${leagueId}`);
      }
      
      // Migrate team count
      const teamCount = this.getTeamCountFromLocalStorage(leagueId);
      if (teamCount) {
        await this.setTeamCount(leagueId, teamCount, currentUser);
        devLog(`Migrated team count for league ${leagueId}`);
      }
      
      // Migrate draft order
      const draftOrder = this.getDraftOrderFromLocalStorage(leagueId);
      if (draftOrder && draftOrder.length > 0) {
        await this.setDraftOrder(leagueId, draftOrder, currentUser);
        devLog(`Migrated draft order for league ${leagueId}`);
      }
      
      // Migrate draft timer
      const timerData = this.getDraftTimerFromLocalStorage(leagueId);
      const timerDuration = this.getDraftTimerDurationFromLocalStorage(leagueId);
      if (timerData || timerDuration) {
        const combinedTimerData = {
          ...timerData,
          duration: timerDuration || timerData?.duration || 60
        };
        await this.setDraftTimer(leagueId, combinedTimerData, currentUser);
        devLog(`Migrated draft timer for league ${leagueId}`);
      }
      
      // Migrate draft chat messages
      const chatMessages = this.getDraftChatFromLocalStorage(leagueId);
      if (chatMessages && chatMessages.length > 0) {
        for (const message of chatMessages) {
          await this.addDraftChatMessage(leagueId, message.message, currentUser);
        }
        devLog(`Migrated ${chatMessages.length} draft chat messages for league ${leagueId}`);
      }
      
      // Migrate week processing data
      const weekProcessing = this.getWeekProcessingFromLocalStorage(leagueId);
      if (weekProcessing) {
        await this.setWeekProcessing(leagueId, weekProcessing, currentUser);
        devLog(`Migrated week processing data for league ${leagueId}`);
      }
      
      // Migrate week records data
      const weekRecords = this.getWeekRecordsFromLocalStorage(leagueId);
      if (weekRecords) {
        await this.setWeekRecords(leagueId, weekRecords, currentUser);
        devLog(`Migrated week records data for league ${leagueId}`);
      }
      
      // Migrate team rosters data
      for (let i = 1; i <= 20; i++) { // Support up to 20 teams
        const teamName = `Team${i}`;
        const teamRoster = this.getTeamRosterFromLocalStorage(leagueId, teamName);
        if (teamRoster && teamRoster.length > 0) {
          await this.setTeamRoster(leagueId, teamName, teamRoster, currentUser);
          devLog(`Migrated team roster data for ${teamName} in league ${leagueId}`);
        }
      }
      
      // Migrate team names data
      for (let i = 1; i <= 20; i++) { // Support up to 20 teams
        const teamName = `Team${i}`;
        const teamDisplayName = this.getTeamNameFromLocalStorage(leagueId, teamName);
        if (teamDisplayName) {
          await this.setTeamCustomization(leagueId, teamName, teamDisplayName, null, currentUser);
          devLog(`Migrated team name data for ${teamName} in league ${leagueId}`);
        }
      }
      
      // Migrate FAAB data
      for (let i = 1; i <= 20; i++) { // Support up to 20 teams
        const teamName = `Team${i}`;
        
        // Migrate FAAB budget
        const faabBudget = this.getFaabBudgetFromLocalStorage(teamName);
        if (faabBudget !== null) {
          try {
            // Use the existing FAAB API to set the budget
            const faabApi = await import('./faabApi.js');
            await faabApi.default.initializeFaabBudgets(leagueId, currentUser);
            devLog(`Migrated FAAB budget for ${teamName} in league ${leagueId}`);
          } catch (e) {
            console.error(`Error migrating FAAB budget for ${teamName}:`, e);
          }
        }
        
        // Migrate FAAB bids
        const faabBids = this.getFaabBidsFromLocalStorage(teamName);
        if (faabBids && faabBids.length > 0) {
          try {
            const faabApi = await import('./faabApi.js');
            for (const bid of faabBids) {
              await faabApi.default.createFaabTransaction(leagueId, {
                team_name: teamName,
                transaction_type: 'bid',
                amount: bid.amount,
                player_name: bid.playerName,
                description: `Migrated bid: $${bid.amount} for ${bid.playerName}`
              }, currentUser);
            }
            devLog(`Migrated ${faabBids.length} FAAB bids for ${teamName} in league ${leagueId}`);
          } catch (e) {
            console.error(`Error migrating FAAB bids for ${teamName}:`, e);
          }
        }
        
        // Migrate FAAB history
        const faabHistory = this.getFaabHistoryFromLocalStorage(teamName);
        if (faabHistory && faabHistory.length > 0) {
          try {
            const faabApi = await import('./faabApi.js');
            for (const transaction of faabHistory) {
              await faabApi.default.createFaabTransaction(leagueId, {
                team_name: teamName,
                transaction_type: transaction.type === 'bid' ? 'bid' : 'refund',
                amount: transaction.amount,
                player_name: transaction.player,
                description: `Migrated transaction: ${transaction.description || transaction.type}`
              }, currentUser);
            }
            devLog(`Migrated ${faabHistory.length} FAAB history items for ${teamName} in league ${leagueId}`);
          } catch (e) {
            console.error(`Error migrating FAAB history for ${teamName}:`, e);
          }
                 }
       }
       
       // Migrate waiver data
       for (let i = 1; i <= 20; i++) { // Support up to 20 teams
         const teamName = `Team${i}`;
         const waiverHistory = this.getWaiverHistoryFromLocalStorage(teamName);
         if (waiverHistory && waiverHistory.length > 0) {
           try {
             for (const transaction of waiverHistory) {
               await this.createWaiverTransaction(leagueId, {
                 team_name: teamName,
                 transaction_type: transaction.type || 'add',
                 player_name: transaction.player || transaction.playerName,
                 player_id: transaction.playerId || transaction.player_id,
                 week: transaction.week,
                 description: transaction.description || `Migrated waiver transaction: ${transaction.type}`
               }, currentUser);
             }
             devLog(`Migrated ${waiverHistory.length} waiver history items for ${teamName} in league ${leagueId}`);
           } catch (e) {
             console.error(`Error migrating waiver history for ${teamName}:`, e);
           }
         }
       }
       
       // Migrate week processing data
       for (let week = 1; week <= 17; week++) {
         const weekProcessed = this.getWeekProcessedFromLocalStorage(leagueId, week);
         if (weekProcessed) {
           try {
             await this.setWeekProcessed(leagueId, week, true, currentUser);
             devLog(`Migrated week ${week} processed status for league ${leagueId}`);
           } catch (e) {
             console.error(`Error migrating week ${week} status:`, e);
           }
         }
       }
       
       // Migrate random player points data
       // This is more complex as we need to scan all localStorage keys
       try {
         const allKeys = Object.keys(localStorage);
         const randomPointsKeys = allKeys.filter(key => 
           key.startsWith(`ffb_rand_${leagueId}_`) && 
           key.includes('_') && 
           key.split('_').length >= 6
         );
         
         for (const key of randomPointsKeys) {
           try {
             const parts = key.split('_');
             const teamName = parts[3];
             const week = parseInt(parts[4]);
             const slot = parts[5];
             const playerId = parts.slice(6).join('_'); // Player ID might contain underscores
             
             const pointsData = this.getRandomPlayerPointsFromLocalStorage(leagueId, teamName, week, slot, playerId);
             if (pointsData) {
               await this.setRandomPlayerPoints(leagueId, {
                 team_name: teamName,
                 week: week,
                 slot: slot,
                 player_id: playerId,
                 season: pointsData.season,
                 points: pointsData.points,
                 raw_stats: pointsData.rawStats
               }, currentUser);
               devLog(`Migrated random player points for ${teamName} week ${week} slot ${slot} player ${playerId}`);
             }
           } catch (e) {
             console.error(`Error migrating random player points for key ${key}:`, e);
           }
         }
         
         if (randomPointsKeys.length > 0) {
           devLog(`Migrated ${randomPointsKeys.length} random player points records for league ${leagueId}`);
         }
       } catch (e) {
         console.error('Error migrating random player points data:', e);
       }
       
       // Remove localStorage data after successful migration
       this.removeLocalStorageData(leagueId);
      
      devLog(`Completed localStorage migration for league ${leagueId}`);
      return true;
    } catch (error) {
      console.error(`Error migrating localStorage data for league ${leagueId}:`, error);
      return false;
    }
  }
}

const draftStateApi = new DraftStateApi();
export default draftStateApi;
